/**
 * license.ts — the VSKI License text + the acceptance-cookie helpers.
 *
 * The text is the VSKI License (v1.0) issued by VSKI LLC (the company
 * requisites from the corporate site: legal name, address, contact email).
 * Until the visitor accepts it, `LicenseGate` blocks the app behind an
 * opaque backdrop; acceptance is recorded in a cookie that every API request
 * carries (see patchFetch.ts — the browser only sends cookies on cross-origin
 * fetches when `credentials: 'include'` is set).
 */

/** The cookie that records license acceptance. Presence = accepted. */
export const LICENSE_COOKIE = 'vski-license';

/** The license shown in the gate modal. Keep byte-for-byte in sync with the
 *  corporate license text — it is a legal notice, not prose to edit. */
export const LICENSE_TEXT = `VSKI License v1.0

Copyright (c) 2026 VSKI LLC <info@vski.tech>, 30 N Gould St Ste R, Sheridan, WY 82801, USA

This VSKI License (the "License") governs your use of the software provided hereunder (the "Software"). By using, copying, modifying, or distributing the Software, you accept and agree to be bound by the terms of this License.

1. Definitions

"Entity" shall mean any individual, corporation, partnership, or other legal entity, and includes all other entities that control, are controlled by, or are under common control with that entity.

"Annual Gross Revenue" shall mean all revenue received by an Entity from all sources worldwide during its most recently completed fiscal year, measured in United States Dollars ($USD).

"Total Capital Raised" shall mean the cumulative total of all funds received by an Entity in exchange for equity, debt instruments convertible to equity, grants, or any other form of investment, measured in United States Dollars ($USD).

"Derivative Work" shall mean:

(i) any work based on the Software, including any modification, translation, or adaptation that incorporates a substantial portion of the Software's source code; or

(ii) any work, in whole or in part, generated by an artificial intelligence system where the Software (or any portion thereof) was used as input, training data, or reference.


"Excluded Work" shall mean a separate and independent software work, such as a third-party plugin or extension, which interoperates with the Software through its designated Application Programming Interfaces (APIs) but is not otherwise a Derivative Work.

"Integration" means the act of connecting, linking, merging, or interfacing the Software or a Derivative Work with any other software, application, or system. This includes, but is not limited to, the use of Application Programming Interfaces (APIs) or any other method that enables interoperation.

2. Free License Grant

Subject to the terms and conditions of this License, a perpetual, worldwide, non-exclusive, royalty-free, irrevocable license is hereby granted to any Entity that meets neither of the conditions set forth in Section 3, to use, copy, modify, merge, publish, and/or distribute copies of the Software.

3. Commercial License Required

Use, modification, or distribution of the Software by or for any Entity is prohibited without a separate, paid commercial license issued by this License copyright holders if the Entity meets either of the following conditions:

a. Its Annual Gross Revenue is one million US dollars ($1,000,000 USD) or more; OR

b. Its Total Capital Raised is one million US dollars ($1,000,000 USD) or more.

The commercial licenses may only be issued by this VSKI License copyright holders. Creators of any Derivative Work are expressly prohibited from granting commercial licenses for such works.

Terms, costs, and additional agreements for commercial licenses may be negotiated individually.

4. Copyleft and Distribution Conditions

Any distribution of the Software, or any Derivative Work, is subject to the following conditions:

a. It must be licensed under the terms of this VSKI License.

b. The above copyright notice and the full text of this License must be included in all copies or substantial portions of the Software or Derivative Work.

c. For the avoidance of doubt, creating and distributing an Excluded Work that interoperates with the Software does not, by itself, subject the Excluded Work to the terms of this License.

d. For the avoidance of doubt, the Integration of the Software is not considered Excluded Work. Any such Integration remains subject to all terms and conditions set in Section 3.

e. Providing the functionality of the Software or a Derivative Work as part of a commercial Software-as-a-Service (SaaS) product constitutes distribution and is subject to the conditions defined in Section 3.


6. Disclaimer of Warranty

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.`;

/** Read a named cookie's value from document.cookie ('' when absent). */
function readCookie(name: string): string {
  const prefix = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const kv = part.trim();
    if (kv.startsWith(prefix)) return kv.slice(prefix.length);
  }
  return '';
}

/** True when the visitor has accepted the license (the cookie is present). */
export function licenseAccepted(): boolean {
  return readCookie(LICENSE_COOKIE) !== '';
}

/** Record acceptance: set the cookie for one year, on every path. On HTTPS we
 *  also mark it SameSite=None; Secure so the backend (often on another origin
 *  of the same deployment) receives it with `credentials: 'include'` fetches.
 *  SameSite=None without Secure is rejected by browsers outright, and plain
 *  http (local dev) is always same-site, so the attribute rides https only. */
export function acceptLicense(): void {
  const sameSite = location.protocol === 'https:' ? '; SameSite=None; Secure' : '';
  document.cookie = `${LICENSE_COOKIE}=accepted; path=/; max-age=31536000${sameSite}`;
}
